import { browserLocalPersistence, getAuth, onAuthStateChanged, setPersistence, signOut } from 'firebase/auth';
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { verifyTokenValidity } from '../../api/auth/verifyToken';
import { AUTH_TOKEN_KEY } from '../constants';

interface AuthContextProps {
	isAuthenticated: boolean;
	isOnboarded: boolean;
	token: string;
	email: string;
	uid: string;
	signInProvider: string;
	persistAuth: (token: string) => void;
	setIsOnboarded: (isOnboarded: boolean) => void;
	logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuthContext = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuthContext must be used within an AuthProvider');
	}
	return context;
};

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
	const [isOnboarded, setIsOnboarded] = useState<boolean>(false);
	const [token, setToken] = useState<string>('');
	const [email, setEmail] = useState<string>('');
	const [uid, setUid] = useState<string>('');
	const [signInProvider, setSignInProvider] = useState<string>('');

	/*useEffect(() => {
		verifyToken();

		// const handleAuthStateChanged = onAuthStateChanged(auth, async (user) => {
		// 	console.debug('Auth state changed, refreshing token...');
		// 	if (user) {
		// 		const token = await user.getIdToken(); // gets new token if expired
		// 		const email = user.email;
		// 		const uid = user.uid;

		// 		// Save token to state, NOT localStorage
		// 		setToken(token);
		// 		if (email) {
		// 			setEmail(email);
		// 		}
		// 		setUid(uid);
		// 		setIsAuthenticated(true);

		// 		localStorage.setItem(AUTH_TOKEN_KEY, token);
		// 	}
		// });

		// return () => handleAuthStateChanged();
	}, []);*/

	const verifyToken = async () => {
		try {
			const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
			if (!storedToken) throw new Error('Token not found in local storage.');

			const verifyTokenResponse = await verifyTokenValidity(storedToken);
			if (!verifyTokenResponse) throw new Error('Failed to verify token');

			setToken(verifyTokenResponse.token);
			setEmail(verifyTokenResponse.email);
			setUid(verifyTokenResponse.uid);
			setSignInProvider(verifyTokenResponse.signInProvider);

			localStorage.setItem(AUTH_TOKEN_KEY, verifyTokenResponse.token);

			setIsOnboarded(verifyTokenResponse.isOnboarded);
			setIsAuthenticated(true);
		} catch (error: any) {
			console.debug('Token validation failed:', error.message);
			localStorage.removeItem(AUTH_TOKEN_KEY);
			setIsAuthenticated(false);
		}
	};

	const persistAuth = useCallback(async (token: string) => {
		localStorage.setItem(AUTH_TOKEN_KEY, token);
		await verifyToken();
	}, []);

	const logout = useCallback(() => {
		const auth = getAuth();

		// Sign out from Firebase Auth
		signOut(auth)
			.then(() => {
				console.debug('Firebase sign out successful');
			})
			.catch((error) => {
				console.error('Error signing out from Firebase:', error);
			});

		// Clear local state and localStorage
		localStorage.removeItem(AUTH_TOKEN_KEY);
		setIsAuthenticated(false);
		setIsOnboarded(false);
		setUid('');
		setToken('');
		setEmail('');
		setSignInProvider('');
	}, []);

	useEffect(() => {
		const auth = getAuth();

		setPersistence(auth, browserLocalPersistence)
			.then(() => {
				const unsubscribe = onAuthStateChanged(auth, async (user) => {
					if (user) {
						try {
							const idToken = await user.getIdToken(true); // force refresh if expired
							await persistAuth(idToken); // internally calls verifyToken
						} catch (error) {
							console.error('Error refreshing ID token:', error);
						}
					} else {
						logout(); // clear everything if user logs out
					}
				});
				return () => unsubscribe();
			})
			.catch((err) => {
				console.error('Failed to set persistence:', err);
			});
	}, [persistAuth, logout]);

	/*const verifyToken = async () => {
		try {
			const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
			if (!storedToken) {
				throw new Error('Token not found in local storage.');
			}

			const verifyTokenResponse = await verifyTokenValidity(storedToken);

			if (!verifyTokenResponse) {
				throw new Error('Failed to verify token');
			}
			
			setToken(verifyTokenResponse.token);
			setEmail(verifyTokenResponse.email);
			setUid(verifyTokenResponse.uid);
			setSignInProvider(verifyTokenResponse.signInProvider);

			localStorage.setItem(AUTH_TOKEN_KEY, verifyTokenResponse.token);

			setIsOnboarded(verifyTokenResponse.isOnboarded);
			setIsAuthenticated(true);
		} catch (error: any) {
			console.debug('Token validation failed:', error.message);

			if (error.statusCode === 401) {
				console.warn('Unauthorized: Removing invalid token');
				localStorage.removeItem(AUTH_TOKEN_KEY);
			}
		}
	};*/

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				isOnboarded,
				token,
				email,
				uid,
				signInProvider,
				persistAuth,
				setIsOnboarded,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};
